import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
	type AppState,
	defaultFiatMetaProps,
	getTargetEnvironment,
	type IAuthSettings,
	type IBeneficiary,
	type IBusiness,
	type ICountDownStartArray,
	type IFiatMetaProps,
	type IMeta,
	type IUser,
	type IUserPreference,
	type IUserWallet,
	LIVE_ENVIRONMENT,
	type NotificationPayload,
	type TargetEnvTypes,
	type TourStep,
} from 'BreetHelpers';

import { reduxPersistor } from '@/lib';

const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
const persistedState = typeof window !== 'undefined' ? reduxPersistor.getData('app') : null;

const getAppThemeState = () => {
	if (persistedState?.themeMode) return persistedState.themeMode;
	return prefersDark ? 'dark' : 'light';
};

const initialState: AppState = {
	themeMode: getAppThemeState(),
	isUserLoggedIn: persistedState?.isUserLoggedIn ?? false,
	fiatMeta: defaultFiatMetaProps,
	meta: undefined,
	wallet: [],
	beneficiaries: [],
	preference: undefined,
	accessCredentialEvents: [],
	userData: undefined,
	mobileMenuOpen: undefined,
	countDownStartTimeArray: persistedState?.countDownStartTimeArray ?? [],
	lastVisited: persistedState?.lastVisited,
	isFiatBalanceHidden: persistedState?.isFiatBalanceHidden,
	showSecretKey: persistedState?.showSecretKey,
	isTokenFound: false,
	latestNotification: null,
	fcmToken: persistedState?.fcmToken,
	is2faAlert: false,
	targetEnvironment: getTargetEnvironment(),
	isApprovedForLive: persistedState?.isApprovedForLive ?? false,
	business: persistedState?.business ?? null,
	exportEndDate: undefined,
	exportStartDate: undefined,
	tourStep: null,
};

// Full reset applied on logout. Only the device theme is preserved (lastVisited is set from the action).
// Everything user-scoped — balances, beneficiaries, preferences, credential codes, prefs — is wiped so it
// can't leak into the next account on the same browser.
const loggedOutState: Omit<AppState, 'themeMode' | 'lastVisited'> = {
	isUserLoggedIn: false,
	fiatMeta: defaultFiatMetaProps,
	meta: undefined,
	wallet: [],
	beneficiaries: [],
	preference: undefined,
	accessCredentialEvents: [],
	userData: undefined,
	mobileMenuOpen: undefined,
	selectedBeneficiary: undefined,
	authSettings: undefined,
	countDownStartTimeArray: [],
	isTokenFound: false,
	latestNotification: null,
	fcmToken: undefined,
	isFiatBalanceHidden: undefined,
	showSecretKey: undefined,
	is2faAlert: false,
	targetEnvironment: LIVE_ENVIRONMENT,
	isApprovedForLive: false,
	exportStartDate: undefined,
	exportEndDate: undefined,
	tourStep: null,
	business: null,
};

export const appSlice = createSlice({
	name: 'app',
	initialState,
	reducers: (create) => ({
		setIs2faAlert: create.reducer((state, action: PayloadAction<boolean | undefined>) => {
			state.is2faAlert = action.payload;
		}),
		setThemeMode: create.reducer((state, action: PayloadAction<'dark' | 'light'>) => {
			state.themeMode = action.payload;
		}),
		setTargetEnvironment: create.reducer((state, action: PayloadAction<TargetEnvTypes>) => {
			state.targetEnvironment = action.payload;
		}),
		setIsApprovedForLive: create.reducer((state, action: PayloadAction<boolean>) => {
			state.isApprovedForLive = action.payload;
		}),
		setBusiness: create.reducer((state, action: PayloadAction<IBusiness | null>) => {
			state.business = action.payload;
		}),
		setShowSecretKey: create.reducer((state, action: PayloadAction<boolean>) => {
			state.showSecretKey = action.payload;
		}),
		setCountDownStartTimeArray: create.reducer((state, action: PayloadAction<ICountDownStartArray[]>) => {
			state.countDownStartTimeArray = action.payload;
		}),
		clearCountDownStartTimeArray: create.reducer((state) => {
			state.countDownStartTimeArray = [];
		}),
		setFiatMeta: create.reducer((state, action: PayloadAction<IFiatMetaProps>) => {
			state.fiatMeta = action.payload;
		}),
		setAuthSettings: create.reducer((state, action: PayloadAction<IAuthSettings | undefined>) => {
			state.authSettings = action.payload;
		}),
		setUserData: create.reducer((state, action: PayloadAction<IUser | undefined>) => {
			state.userData = action.payload;
		}),
		setMobileMenuOpen: create.reducer((state, action: PayloadAction<boolean>) => {
			state.mobileMenuOpen = action.payload;
		}),
		setMeta: create.reducer((state, action: PayloadAction<IMeta>) => {
			state.meta = action.payload;
		}),
		setWallet: create.reducer((state, action: PayloadAction<IUserWallet[]>) => {
			state.wallet = action.payload;
		}),
		resolveAccessCredentials: create.reducer((state, action: PayloadAction<{ verificationId: string; code: string }>) => {
			state.accessCredentialEvents.push(action.payload);
		}),
		consumeAccessCredentialEvent: create.reducer((state, action: PayloadAction<string>) => {
			state.accessCredentialEvents = state.accessCredentialEvents.filter((event) => event.verificationId !== action.payload);
		}),
		setBeneficiaries: create.reducer((state, action: PayloadAction<IBeneficiary[]>) => {
			state.beneficiaries = action.payload;
		}),
		setSelectedBeneficiary: create.reducer((state, action: PayloadAction<IBeneficiary | undefined>) => {
			state.selectedBeneficiary = action.payload;
		}),
		setPreference: create.reducer((state, action: PayloadAction<IUserPreference | undefined>) => {
			state.preference = action.payload;
		}),
		logUserIn: create.reducer((state) => {
			state.isUserLoggedIn = true;
		}),
		logUserOut: create.reducer((state, action: PayloadAction<string | undefined>) => ({
			...loggedOutState,
			themeMode: state.themeMode,
			lastVisited: action.payload,
		})),
		setIsFiatBalanceHidden: (state, action: PayloadAction<boolean | undefined>) => {
			state.isFiatBalanceHidden = action.payload;
		},
		setLatestNotification: (state, action: PayloadAction<NotificationPayload | null>) => {
			state.latestNotification = action.payload;
		},
		setFcmToken: (state, action: PayloadAction<string>) => {
			state.fcmToken = action.payload;
		},
		setExportStartDate: (state, action: PayloadAction<string>) => {
			state.exportStartDate = action.payload;
		},
		setExportEndDate: (state, action: PayloadAction<string>) => {
			state.exportEndDate = action.payload;
		},
		resetExportDates: (state) => {
			if (state.exportStartDate === undefined && state.exportEndDate === undefined) return;
			state.exportStartDate = undefined;
			state.exportEndDate = undefined;
		},
		setTourStep: (state, action: PayloadAction<TourStep | null>) => {
			state.tourStep = action.payload;
		},
	}),
	selectors: {
		selectThemeMode: (state) => state.themeMode,
		selectFiatMeta: (state) => state.fiatMeta,
		selectMeta: (state) => state.meta,
		selectMobileMenuOpen: (state) => state.mobileMenuOpen,
		selectWallet: (state) => state.wallet,
		selectBeneficiaries: (state) => state.beneficiaries,
		selectPreference: (state) => state.preference,
		selectAccessCredentialEvents: (state) => state.accessCredentialEvents,
		selectUserData: (state) => state.userData,
		selectSelectedBeneficiary: (state) => state.selectedBeneficiary,
		selectAuthSettings: (state) => state.authSettings,
		selectCountDownStartTimeArray: (state) => state.countDownStartTimeArray,
		selectIsUserLoggedIn: (state) => state.isUserLoggedIn,
		selectLastVisited: (state) => state.lastVisited,
		selectLatestNotification: (state) => state.latestNotification,
		selectFcmToken: (state) => state.fcmToken,
		selectIsFiatBalanceHidden: (state) => state.isFiatBalanceHidden,
		selectShowSecretKey: (state) => state.showSecretKey,
		selectIs2faAlert: (state) => state.is2faAlert,
		selectTargetEnvironment: (state) => state.targetEnvironment,
		selectIsApprovedForLive: (state) => state.isApprovedForLive,
		selectBusiness: (state) => state.business,
		selectExportStartDate: (state) => state.exportStartDate,
		selectExportEndDate: (state) => state.exportEndDate,
		selectTourStep: (state) => state.tourStep,
	},
});

export const {
	setThemeMode,
	setFiatMeta,
	setBeneficiaries,
	setWallet,
	setMeta,
	setPreference,
	logUserIn,
	logUserOut,
	resolveAccessCredentials,
	consumeAccessCredentialEvent,
	setUserData,
	setMobileMenuOpen,
	setSelectedBeneficiary,
	setAuthSettings,
	setCountDownStartTimeArray,
	clearCountDownStartTimeArray,
	setLatestNotification,
	setFcmToken,
	setIsFiatBalanceHidden,
	setShowSecretKey,
	setIs2faAlert,
	setTargetEnvironment,
	setIsApprovedForLive,
	setBusiness,
	setExportStartDate,
	setExportEndDate,
	resetExportDates,
	setTourStep,
} = appSlice.actions;

export const {
	selectThemeMode,
	selectFiatMeta,
	selectMeta,
	selectPreference,
	selectWallet,
	selectBeneficiaries,
	selectAccessCredentialEvents,
	selectUserData,
	selectMobileMenuOpen,
	selectSelectedBeneficiary,
	selectAuthSettings,
	selectCountDownStartTimeArray,
	selectIsUserLoggedIn,
	selectLastVisited,
	selectLatestNotification,
	selectFcmToken,
	selectIsFiatBalanceHidden,
	selectShowSecretKey,
	selectIs2faAlert,
	selectTargetEnvironment,
	selectIsApprovedForLive,
	selectBusiness,
	selectExportStartDate,
	selectExportEndDate,
	selectTourStep,
} = appSlice.selectors;
